import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import FilterRangeSelector from 'ui-component/filter-range/FilterRangeSelector';
import moment from 'moment';
import { Skeleton, Tooltip, Avatar } from '@mui/material';

import { FaSpinner, FaTimes } from 'react-icons/fa';
import { FaFilter } from 'react-icons/fa6';
import { CommonDateOnlyFormat } from 'helpers/common.helper';
import { PiInfoFill, PiMicrosoftExcelLogoFill, PiMicrosoftExcelLogo, PiFilePdf } from 'react-icons/pi';
import { MdKeyboardArrowDown, MdOutlineSettingsBackupRestore } from 'react-icons/md';
import * as XLSX from 'xlsx';
import usePageTitle from 'hooks/usePageTitle';
// import DemographicChartComponent from '../dashboard/Default/components/DemographicChartComponent';
import SortableHeader from 'ui-component/SortableHeader';
import NoRecordFoundIcon from 'assets/images/icons/no-records-found.svg';
import useIsMobile from 'utils/isMobileUtils';
import { getCurrencySymbol, getCurrencySymbolV2, numberFormatter } from 'utils/currency';

const ContactRevenueDetails = () => {
    const [setPageTitle] = usePageTitle('Contact Revenue');
    const statusTypes = useSelector((state) => state.main.statusTypes);
    const statusTypeList = statusTypes?.map((item) => ({ id: item.id, name: item.name }));
    const selectedCurrency = useSelector((state) => state.main.selectedCurrency);

    const [showFilterDateUI, setShowFilterDateUI] = useState(false);

    const [dataList, setDataList] = useState([]);
    const tableRef = useRef();


    const [selectedFilterBy, setSelectedFilterBy] = useState('MONTH');
    const [defaultSelectedStartYear, setDefaultSelectedStartYear] = useState(0);
    const [defaultSelectedEndYear, setDefaultSelectedEndYear] = useState(0);
    const [defaultSelectedStartMonth, setDefaultSelectedStartMonth] = useState(0);
    const [defaultSelectedEndMonth, setDefaultSelectedEndMonth] = useState(0);
    const [defaultSelectedContactId, setDefaultSelectedContactId] = useState(null);
    const [defaultSelectedContactName, setDefaultSelectedContactName] = useState('');

    const [searchParams] = useSearchParams();

    
    useEffect(() => {
        const defaultFilterBy = searchParams.get('filterBy') || 'MONTH';
        const defaultStartYear = parseInt(searchParams.get('startYear')) || (moment().year());
        const defaultStartMonth = parseInt(searchParams.get('startMonth')) || 1;
        const defaultEndYear = parseInt(searchParams.get('endYear')) || moment().year();
        const defaultEndMonth = parseInt(searchParams.get('endMonth')) || (moment().month() + 1);
        const defaultContactId = parseInt(searchParams.get('contactId')) || null;
        const defaultContactName = searchParams.get('contactName') || '';

        setSelectedFilterBy(defaultFilterBy);
        setDefaultSelectedStartYear(defaultStartYear);
        setDefaultSelectedStartMonth(defaultStartMonth);
        setDefaultSelectedEndYear(defaultEndYear);
        setDefaultSelectedEndMonth(defaultEndMonth);
        setDefaultSelectedContactId(defaultContactId);
        setDefaultSelectedContactName(defaultContactName);
        processReport(defaultContactId, defaultStartYear, defaultEndYear, defaultStartMonth, defaultEndMonth, defaultFilterBy);
    }, [searchParams])

    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    const openInNewTab = (url) => {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
    };

    const viewDetails = (id) => {
        openInNewTab(`/sales/invoices/${id}`);
    };

    const isMobile = useIsMobile();

    const [total, setTotal] = useState({
        eur: 0,
        usd: 0,
        aed: 0,
        all: 0
    });

    const processReport = (contactId, startYear, endYear, startMonth, endMonth, filterBy) => {
        setIsLoading(true);
        const request = {
            contact_id: contactId,
            filter_by: filterBy,
            start_year: startYear,
            end_year: endYear,
            start_month: startMonth,
            end_month: endMonth
        }
        
    api.generateContactRevenueDetailsReport(request).then((res) => {
            setIsLoading(false);
            if (res?.status) {
                // setReportData(res.data); 
                const overAllAmountEur = res?.data?.reduce((sum, item) => sum + parseFloat(item?.converted_currency_total?.eur || 0), 0);
                const overAllAmountUsd = res?.data?.reduce((sum, item) => sum + parseFloat(item?.converted_currency_total?.usd || 0), 0);
                const overAllAmountAed = res?.data?.reduce((sum, item) => sum + parseFloat(item?.converted_currency_total?.aed || 0), 0);
                const overAllAmountAll = res?.data?.reduce((sum, item) => sum + parseFloat(item?.converted_currency_total?.all || 0), 0);
                
                setTotal({
                    ...total,
                    eur: overAllAmountEur,
                    usd: overAllAmountUsd,
                    aed: overAllAmountAed,
                    all: overAllAmountAll
                });
                setDataList(res?.data)
            }
        }).catch((error) => {
            setIsLoading(false);
            console.error('Error fetching Profit and Loss Statement Report:', error);
        });

    }

    const formatMonth = (date) => date.toLocaleString("en-US", { month: "short", year: "numeric" });

    const formatQuarter = (date) => {
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return `Q${quarter} ${date.getFullYear()}`;
    };

    const formatYear = (date) => date.getFullYear();
    const dateFilterTextValue = useMemo(() => {
        if (!defaultSelectedStartYear || !defaultSelectedEndYear || !defaultSelectedStartMonth || !defaultSelectedEndMonth) return "";
        
        const startDate = new Date(defaultSelectedStartYear, defaultSelectedStartMonth, 0);
        const endDate = new Date(defaultSelectedEndYear, defaultSelectedEndMonth, 0);
        switch (selectedFilterBy) {
            case "MONTH":
            return `${formatMonth(startDate) === formatMonth(endDate) ? formatMonth(startDate) : `${formatMonth(startDate)} - ${formatMonth(endDate)}`}`;

            case "QUARTER":
            return `${formatQuarter(startDate)} - ${formatQuarter(endDate)}`;

            case "YEAR":
                const yearValue = defaultSelectedStartYear === defaultSelectedEndYear ? formatYear(startDate) : `${formatYear(startDate)} - ${formatYear(endDate)}`;
            return yearValue;

            default:
            return "";
        }
    }, [selectedFilterBy, defaultSelectedStartYear, defaultSelectedEndYear, defaultSelectedStartMonth, defaultSelectedEndMonth]);
    


    const s2ab = (s) => {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
    };
    // Utility function to remove all special characters and replace with space
    const cleanString = (str) => str ? str.replace(/[^a-zA-Z0-9 ]/g, ' ') : '';

    const buildExcelRows = (data) => {
        const rows = [];

        data.forEach(inv => {
            // Header row (invoice summary)
            let currecySymbol = '$';
            if (selectedCurrency === 'usd') {
                currecySymbol = '$';
            } else if (selectedCurrency === 'eur') {
                currecySymbol = '€';
            } else if (selectedCurrency === 'aed') {
                currecySymbol = 'AED';
            } else {
                currecySymbol = '$';
            }
            rows.push({
                Date: inv.date,
                Reference: inv.invoice_number !== undefined ? `${inv.invoice_number} • ${cleanString(defaultSelectedContactName)}` : 'Total',
                Qty: '',
                Total: inv.converted_currency_total ? `${currecySymbol} ${inv.converted_currency_total[selectedCurrency === 'all' ? 'usd' : selectedCurrency] ?? '0.00'}` : inv.Total || ''
            });

            // Item rows
            inv.items?.forEach(item => {
                rows.push({
                    Date: '',
                    Reference: `${item.product}`,
                    Qty: `${item.quantity} pcs`,
                    Total: ``
                });
            });

            // Empty row for spacing (optional)
            rows.push({ Date: '', Reference: '', Qty: '', Total: '' });
        });

        return rows;
    };
    const exportReportXlsx = () => {
        const request = {
            filter_by: selectedFilterBy,
            start_year: defaultSelectedStartYear,
            end_year: defaultSelectedEndYear,
            start_month: defaultSelectedStartMonth,
            end_month: defaultSelectedEndMonth,
            contact_id: defaultSelectedContactId,
            type: 'excel',
            currency: selectedCurrency === 'all' ? 'usd' : selectedCurrency
        };
        setIsExporting(true);
        api.downloadContactRevenueDetailsReport(request).then((res) => {
            setIsExporting(false);
            const rows = buildExcelRows(res.data);

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rows, {
                header: ['Date', 'Reference', 'Qty', 'Total'],
                skipHeader: false
            });

            // Column widths (matches Image 1)
            ws['!cols'] = [
                { wch: 15 },
                { wch: 60 },
                { wch: 20 },
                { wch: 20 },
            ];

            XLSX.utils.book_append_sheet(wb, ws, `Contact Revenue`);

            const wbBinaryString = XLSX.write(wb, {
                bookType: 'xlsx',
                type: 'binary'
            });

            const blob = new Blob(
                [s2ab(wbBinaryString)],
                { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
            );

            saveAs(
                blob,
                `Projectmix Contact Revenue - ${cleanString(defaultSelectedContactName)}.xlsx`
            );
        });
    };

    

    return (
        <div className="row justify-content-center m-0">
                
            <div className="medium-sized-container" style={{ maxWidth: '900px' }}>
                <div className="col-xl-12 col-lg-12 p-0">
                    <div className="show-on-mobile new-page-header" style={{ position: 'relative' }}>
                        <div className="header-bubble" />
                        <div className="d-flex flex-column gap-2 " style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
                            <h5 style={{ textAlign: 'center' }}>Contact Revenue <br /> {defaultSelectedContactName}</h5>
                        
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                <button
                                    className="btn btn-primary btn-outlined-main white-var"
                                    onClick={() => {
                                        if (!isLoading) {
                                            setShowFilterDateUI(!showFilterDateUI)
                                        }
                                    }}
                                >
                                    <span className="menuIcon">
                                        <FaFilter style={{ fontSize: '1em' }} />
                                    </span>
                                    <span>Filter</span>
                                </button>
                                <button
                                    className={`btn btn-primary btn-outlined-main white-var ${isExporting ? 'disabled' : ''}`}
                                    onClick={() => {
                                        if (!isExporting) {
                                            exportReportXlsx();
                                        }
                                    }}
                                    disabled={isExporting}
                                >
                                    {
                                        isExporting ? (
                                            <>
                                                <span className="menuIcon">
                                                    <FaSpinner className="fa-spin" style={{ fontSize: '1.4em' }} />
                                                </span>
                                                <span>Exporting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="menuIcon">
                                                    <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                </span>
                                                <span>Export</span>
                                            </>
                                        )
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                    {
                        isMobile && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 10,
                                    paddingTop: 0,
                                    paddingBottom: 5,
                                    paddingLeft: 20,
                                    paddingRight: 20
                                }}
                                className="show-on-mobile"
                            >
                                {
                                    showFilterDateUI && (
                                        <FilterRangeSelector
                                            showFilter={showFilterDateUI}
                                            defaultSelectedStartYear={defaultSelectedStartYear}
                                            defaultSelectedEndYear={defaultSelectedEndYear}
                                            defaultSelectedStartMonth={defaultSelectedStartMonth}
                                            defaultSelectedEndMonth={defaultSelectedEndMonth}
                                            onApplyFilter={(startYear, endYear, startMonth, endMonth, filterBy) => {
                                                setDefaultSelectedStartYear(startYear);
                                                setDefaultSelectedEndYear(endYear);
                                                setDefaultSelectedStartMonth(startMonth);
                                                setDefaultSelectedEndMonth(endMonth);
                                                setSelectedFilterBy(filterBy);
                                                setShowFilterDateUI(false);
                                                processReport(defaultSelectedContactId, startYear, endYear, startMonth, endMonth, filterBy);
                                                // Apply your filtering logic here based on selected dates
                                            }}
                                            showFilterBy={selectedFilterBy}
                                            onFilterVisibility={() => {
                                                setShowFilterDateUI(false);
                                            }}
                                        />
                                    )
                                }
                            </div>
                        )
                    }
                    <div className="section-head-new pl-0 hide-on-mobile" style={{ margin: '10px 0' }}>
                        <div>
                            <button
                                className={`btn btn-primary btn-outlined-main ${isLoading ? 'disabled' : ''}`}
                                style={{
                                    gap: '0',
                                    zIndex: '12'
                                }}
                                onClick={() => {
                                    if (!isLoading) {
                                        setShowFilterDateUI(!showFilterDateUI)
                                    }
                                }}
                            >
                                Revenue for {defaultSelectedContactName} in {dateFilterTextValue}
                                <span className={`button-rotating-arrow ${showFilterDateUI ? 'rotate-180' : ''} `}>
                                    <MdKeyboardArrowDown />
                                </span>
                            </button>
                            {
                                showFilterDateUI && (
                                    <FilterRangeSelector
                                        showFilter={showFilterDateUI}
                                        defaultSelectedStartYear={defaultSelectedStartYear}
                                        defaultSelectedEndYear={defaultSelectedEndYear}
                                        defaultSelectedStartMonth={defaultSelectedStartMonth}
                                        defaultSelectedEndMonth={defaultSelectedEndMonth}
                                        onApplyFilter={(startYear, endYear, startMonth, endMonth, filterBy) => {
                                            setDefaultSelectedStartYear(startYear);
                                            setDefaultSelectedEndYear(endYear);
                                            setDefaultSelectedStartMonth(startMonth);
                                            setDefaultSelectedEndMonth(endMonth);
                                            setSelectedFilterBy(filterBy);
                                            setShowFilterDateUI(false);
                                            processReport(defaultSelectedContactId, startYear, endYear, startMonth, endMonth, filterBy);
                                            // Apply your filtering logic here based on selected dates
                                        }}
                                        showFilterBy={selectedFilterBy}
                                        onFilterVisibility={() => {
                                            setShowFilterDateUI(false);
                                        }}
                                    />
                                )
                            }
                        </div>
                        <div className="actions">
                            
                            {isLoading ? (
                                    <Dropdown
                                        className="btnDel est-btn iconbtn disabled"
                                        align={'end'}
                                        disabled
                                    >
                                        <Dropdown.Toggle as="span">
                                            <Link
                                                to={''}
                                                className="btn btn-primary btn-outlined-main white-var disabled" disabled
                                                style={{ minWidth: 'unset', gap: '5px' }}
                                            >
                                                <span className="menuIcon">
                                                    <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                </span>
                                                <span>Export</span>
                                            </Link>
                                        </Dropdown.Toggle>
                                    </Dropdown>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!isExporting) {
                                            exportReportXlsx();
                                        }
                                    }}
                                    className={`btn btn-primary btn-outlined-main ${isExporting ? 'disabled' : ''}`}
                                    disabled={isExporting}
                                >
                                        
                                    {
                                        isExporting ? (
                                            <>
                                                <span className="menuIcon">
                                                    <FaSpinner className="fa-spin" style={{ fontSize: '1.4em' }} />
                                                </span>
                                                <span>Exporting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="menuIcon">
                                                    <PiMicrosoftExcelLogo style={{ fontSize: '1.4em' }} /> 
                                                </span>
                                                <span>Export</span>
                                            </>
                                        )
                                    }
                                </button>
                            )}
                        </div>
                    </div>
                    <div
                        className={`card shadow-sm  bg-white mb-4 table-card`}
                        style={{
                            height: `${isMobile ? 'calc(100vh - 275px)' : 'auto'}`,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        
                        <div className="estimation-table-area">
                            <div className="table-side">

                                {
                                    !isMobile ? (
                                        <>
                                            <div
                                                className="table-responsive-xl tableData estimationList"
                                                ref={tableRef}
                                                style={{
                                                    height: 'calc(100vh - 200px)',
                                                    overflowY: 'auto',
                                                    scrollbarWidth: 'thin',
                                                    scrollbarColor: '#0966ff transparent'

                                                }}
                                            >
                                                    <table className="table  min-width-unset tbl-space pm-table d-none d-md-table">
                                                        <thead>
                                                            <tr className="sticky-header">
                                                                
                                                                <th scope="col" style={{ width: '150px' }}>Date</th>
                                                                <th scope="col">Reference</th>
                                                                <th scope="col" style={{ width: '150px' }}>Total</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {isLoading ? (
                                                                <>
                                                                    {[...Array(50)].map((e, i) => (
                                                                        <tr style={{ height: '35px' }} key={i}>
                                                                            {[...Array(3)].map((e, i) => (
                                                                                <td style={{ height: '35px' }}>
                                                                                    <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                                </td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {dataList?.length > 0 && (
                                                                        dataList?.map((item, index) => {
                                                                            return (
                                                                                <tr key={index}>
                                                                                
                                                                                    <td
                                                                                        className={'cursor-pointer align-middle font14'}
                                                                                        onClick={(e) => {
                                                                                            viewDetails(item.id);
                                                                                        }}
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                            minHeight: '35px',
                                                                                            position: 'relative'
                                                                                        }}
                                                                                    >
                                                                                        <div
                                                                                            style={{
                                                                                                position: 'absolute',
                                                                                                top: '5px',
                                                                                            }}
                                                                                        >
                                                                                            {`${moment(item?.date).format(CommonDateOnlyFormat)}`}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td
                                                                                        className={'cursor-pointer flex align-middle font14'}
                                                                                        onClick={(e) => {
                                                                                            viewDetails(item.id);
                                                                                        }}
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                            minHeight: '35px',
                                                                                            display: 'block',
                                                                                        }}
                                                                                    >
                                                                                        <div>

                                                                                            <a
                                                                                                href={`/sales/invoices/${item.id}`}
                                                                                                target="_blank"
                                                                                                style={{
                                                                                                    textDecoration: 'none',
                                                                                                    // gap: '5px'
                                                                                                }}
                                                                                                className="flex"
                                                                                            >
                                                                                                <span style={{ width: '90px' }}>INV {item?.invoice_number}</span>
                                                                                                <span style={{ width: '13px' }}> • </span>
                                                                                                <span>{defaultSelectedContactName}</span>
                                                                                                
                                                                                            </a>
                                                                                        </div>
                                                                                        <div>
                                                                                            {
                                                                                                item?.items && item?.items.length > 0 && (
                                                                                                    item?.items.map((itm, idx) => (
                                                                                                        <div key={idx} className="flex text-gray-800">
                                                                                                            <span>{itm?.product}</span>
                                                                                                            <span style={{ width: '15px', textAlign: 'center' }}> • </span>
                                                                                                            <span className="fw-500">{parseInt(itm?.quantity)?.toLocaleString()} pcs</span>
                                                                                                        </div>
                                                                                                    ))
                                                                                                )
                                                                                            }
                                                                                        </div>
                                                                                    </td>
                                                                                    <td
                                                                                        className={'cursor-pointer align-middle font14'}
                                                                                        onClick={(e) => {
                                                                                            viewDetails(item.id);
                                                                                        }}
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                            minHeight: '35px',
                                                                                            position: 'relative'
                                                                                        }}
                                                                                    >
                                                                                        <div
                                                                                            className="flex gap-1"
                                                                                            style={{
                                                                                                position: 'absolute',
                                                                                                top: '5px',
                                                                                            }}
                                                                                        >
                                                                                            <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                                                                {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, item?.currency, '11px', '11px')}
                                                                                            </div>
                                                                                            <div>
                                                                                                {item?.converted_currency_total ? numberFormatter.format(item?.converted_currency_total[selectedCurrency === 'all' ? 'usd' : selectedCurrency]) : '0.00'}
                                                                                            </div>
                                                                                        </div>
                                                                                        
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })
                                                                    )}
                                                                    {(dataList?.length < 1 && !isLoading) && (
                                                                        <tr className="no-hover">
                                                                            <td colSpan={9}>
                                                                                <div className="not-found-container">
                                                                                    <div className="not-found-icon">
                                                                                        <img src={NoRecordFoundIcon} style={{ height: '200px', width: '200px' }} />
                                                                                    </div>
                                                                                    <h6 className="not-found-text">No Data Available</h6>
                                                                                    
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </>
                                                            )}
                                                        </tbody>
                                                </table>
                                            </div>
                                            <div className="flex align-items-center ml-auto" style={{ gap: '5px', paddingRight: '25px', justifyContent: 'space-between', padding: '10px 15px 10px 10px' }}>
                                                <div className="fw-500" style={{ fontSize: '16px' }}>Total</div>
                                                <div className="fw-500" style={{ fontSize: '16px', width: '135px' }}>
                                                    {isLoading ? (
                                                        <div style={{ width: '100px' }}>
                                                            <Skeleton variant="rounded" width={'100%'} height={20}></Skeleton>
                                                        </div>
                                                    ) : (
                                                        <div className="flex gap-1">
                                                            <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                                {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                                            </div>
                                                            <div>
                                                                {total ? numberFormatter.format(total[selectedCurrency === 'all' ? 'usd' : selectedCurrency]) : '0.00'}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        isLoading ? (
                                            <div className="inv-cards-list d-md-none" style={{ padding: '12px 0' }}>
                                                {[...Array(15)].map((_, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="order-list-card"
                                                        style={{
                                                            background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
                                                            marginBottom: 10,
                                                            borderRadius: 12,
                                                            padding: 16,
                                                            minHeight: 90,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 10
                                                        }}
                                                    >
                                                        {[...Array(3)].map((__, i) => (
                                                            <div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
                                                                <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="inv-cards-list d-md-none">
                                            
                                                {dataList?.length > 0 ? (
                                                    dataList.map((item, index) => (
                                                        
                                                        <div
                                                            className="order-list-card active-action"
                                                            style={{
                                                                background: index % 2 === 0 ? '#f9f9f9' : '#fff'
                                                            }}
                                                            onClick={() => viewDetails(item?.id)}
                                                        >
                                                            <div className="order-list-card-header">
                                                                <h6 className="order-no text-primary">
                                                                    INV {item?.invoice_number}
                                                                </h6>
                                                                <p className="order-date">{moment(item?.date).format(CommonDateOnlyFormat)}</p>
                                                            </div>

                                                            <div className="order-list-card-body">
                                                                <p className="total-val">
                                                                
                                                                    <div className="flex gap-1">
                                                                        <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                                            {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, item?.currency, '11px', '11px')}
                                                                        </div>
                                                                        <div>
                                                                            {item?.converted_currency_total ? numberFormatter.format(item?.converted_currency_total[selectedCurrency === 'all' ? 'usd' : selectedCurrency]) : 'N/A'}
                                                                        </div>
                                                                    </div>
                                                                </p>
                                                            </div>
                                                            <div className="order-list-card-body">
                                                                <div className="order-contacts">
                                                                    <div className="order-customer">
                                                                        {
                                                                            item?.items && item?.items.length > 0 && (
                                                                                item?.items.map((itm, idx) => (
                                                                                    <div key={idx} className="flex text-gray-800">
                                                                                        <span>{itm?.product}</span>
                                                                                        <span style={{ width: '15px', textAlign: 'center' }}> • </span>
                                                                                        <span className="fw-500">{parseInt(itm?.quantity)?.toLocaleString()} pcs</span>
                                                                                    </div>
                                                                                ))
                                                                            )
                                                                        }
                                                                    </div>
                                                                </div>
                                                                {/* <p className="total-val">

                                                                    <div className="flex gap-1">
                                                                        <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                                            {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, item?.currency, '11px', '11px')}
                                                                        </div>
                                                                        <div>
                                                                            {item?.converted_currency_total ? numberFormatter.format(item?.converted_currency_total[selectedCurrency === 'all' ? 'usd' : selectedCurrency]) : 'N/A'}
                                                                        </div>
                                                                    </div>
                                                                </p> */}
                                                            </div>

                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ padding: 32, textAlign: 'center', background: '#fff', borderRadius: 12 }}>
                                                        <img src={NoRecordFoundIcon} style={{ height: '120px', width: '120px' }} />
                                                        <div style={{ fontStyle: 'italic', color: '#000', fontSize: '16px', marginTop: 12 }}>No Data Available</div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )
                                }
                                {isLoading ? (
                                    <div className="inv-cards-list d-md-none" style={{ padding: '12px 0' }}>
                                        {[...Array(15)].map((_, idx) => (
                                            <div
                                                key={idx}
                                                className="order-list-card"
                                                style={{
                                                    background: idx % 2 === 0 ? '#f9f9f9' : '#fff',
                                                    marginBottom: 10,
                                                    borderRadius: 12,
                                                    padding: 16,
                                                    minHeight: 90,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 10
                                                }}
                                            >
                                                {[...Array(3)].map((__, i) => (
                                                    <div key={i} style={{ width: i === 0 ? '60%' : i === 1 ? '40%' : '80%' }}>
                                                        <Skeleton variant="rounded" width={'100%'} height={18} style={{ borderRadius: '8px' }} />
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <>
                                        {/* <Pagination pagination={paginationData} data={handleData} onLoading={(e) => {}} reactQuery /> */}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    {
                        isMobile && (
                            <div className="flex align-items-center ml-auto" style={{ gap: '5px', paddingRight: '25px', justifyContent: 'space-between', padding: '10px 25px 10px 25px' }}>
                                <div className="fw-500" style={{ fontSize: '16px' }}>Total</div>
                                <div className="fw-500" style={{ fontSize: '16px' }}>
                                    {isLoading ? (
                                        <div style={{ width: '100px' }}>
                                            <Skeleton variant="rounded" width={'100%'} height={20}></Skeleton>
                                        </div>
                                    ) : (
                                        <div className="flex gap-1">
                                            <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0' }`}}>
                                                {getCurrencySymbolV2(selectedCurrency === 'all' ? 'usd' : selectedCurrency, 'usd', '11px', '11px')}
                                            </div>
                                            <div>
                                                {total ? numberFormatter.format(total[selectedCurrency === 'all' ? 'usd' : selectedCurrency]) : 'N/A'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>
        </div>
    );
};

export default ContactRevenueDetails;
